import { useEffect, useRef } from 'react'
import _L from 'leaflet'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

const L = _L as any

// Same bundler-path workaround already used in LocationPicker.tsx -- Leaflet's
// default marker icon resolves its image paths relative to the page URL,
// which breaks under Vite (shows a broken-image icon) unless built from the
// bundled assets explicitly.
const markerDivIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

interface Customer {
  id: number
  name: string
  district?: string | null
  business_type_name?: string | null
  seller_name?: string | null
  latitude: number | null
  longitude: number | null
}

interface CustomersMapProps {
  customers: Customer[]
  height?: number
}

const LIMA_CENTER: [number, number] = [-12.05, -77.03]

export default function CustomersMap({ customers, height = 400 }: CustomersMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const markersRef = useRef<any>(null)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = L.map(containerRef.current).setView(LIMA_CENTER, 12)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map)

    const markers = L.layerGroup().addTo(map)
    mapRef.current = map
    markersRef.current = markers

    setTimeout(() => map.invalidateSize(), 0)

    return () => {
      map.remove()
      mapRef.current = null
      markersRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!markersRef.current) return
    markersRef.current.clearLayers()

    const located = customers.filter(
      (c) => c.latitude != null && c.longitude != null && !isNaN(Number(c.latitude)) && !isNaN(Number(c.longitude))
    )

    located.forEach((c) => {
      const marker = L.marker([Number(c.latitude), Number(c.longitude)], { icon: markerDivIcon })
      marker.bindPopup(
        `<div>
          <strong>${c.name}</strong><br/>
          ${c.district ? `${c.district}<br/>` : ''}
          ${c.business_type_name ? `${c.business_type_name}<br/>` : ''}
          ${c.seller_name ? `Vendedor: ${c.seller_name}` : ''}
        </div>`
      )
      markersRef.current!.addLayer(marker)
    })

    if (located.length > 0) {
      const bounds = L.latLngBounds(located.map((c) => [Number(c.latitude), Number(c.longitude)]))
      mapRef.current?.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 })
    }
  }, [customers])

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: `${height}px`, borderRadius: '8px' }}
    />
  )
}
